/**
 * Scorecard With Visibility Component
 * Main entry point for scorecard visibility feature
 * Handles mode switching between form view and table view
 */

import { useSelector } from 'react-redux';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import { EmptyResults } from '@/components/ats';
import { CsvFailed } from '@/assets/icons/csv-export';
import { type GroupedScorecards, type ScorecardVisibilityMode } from '../types';
import {
  shouldShowTableView,
  getCurrentUserScorecard,
  filterCurrentInterviewScorecards,
} from '../helpers';
import { ScorecardVisibilityView } from '../components/ScorecardVisibilityView';
import { type InterviewType, type ScorecardType } from '@/types/interview';
// import { useKFlags } from '@/hooks/use-k-flags';

// Import existing components for fallback/never mode
import { AfterInterviewScorecard } from '../../AfterInterviewScorecard';
import { useScorecardHelper } from '../../useScorecardFormHelper';

interface ScorecardWithVisibilityProps {
  interview: InterviewType;
  visibilityMode?: ScorecardVisibilityMode;
  apiGroupedScorecards?: GroupedScorecards;
  hasCurrentUserSubmitted: boolean;
  tabId: string;
  refetchScorecardVisibility: () => void;
}

export const ScorecardWithVisibility = ({
  interview,
  visibilityMode: propVisibilityMode,
  apiGroupedScorecards,
  hasCurrentUserSubmitted,
  tabId,
  refetchScorecardVisibility,
}: ScorecardWithVisibilityProps) => {
  const currUser = useSelector(selectCurrUser);
  // const { feScorecardVisibility } = useKFlags();
  const visibilityMode = propVisibilityMode || 'never';
  const currentUserId = currUser?.id;

  const { scorecards: interviewScorecards, meeting_status } =
    (interview as InterviewType) || {};

  const isCandidateNoShowed = meeting_status === 'candidate_no_show';
  const hasNoScorecards = !interviewScorecards?.length;

  const currentUserScorecard = getCurrentUserScorecard(
    apiGroupedScorecards?.currentInterview ?? [],
    currentUserId
  );

  const hasCurrentUserScorecard = !!currentUserScorecard;

  const {
    canEditOthersScorecard,
    isSomeOtherUserHasDraftScorecardForCurrentScorecard,
  } = useScorecardHelper({
    interview,
    scorecards:
      apiGroupedScorecards?.currentInterview as unknown as ScorecardType[],
  });

  // If the current user has a scorecard, we don't need to show the table view in the scorecard tab
  // This is irrespective of the user role, so we need to check for hasCurrentUserScorecard
  const useTableView =
    !hasCurrentUserScorecard && shouldShowTableView(visibilityMode, tabId);

  // if the current user has a scorecard, we need to filter it out
  const currentInterviewScorecards = filterCurrentInterviewScorecards(
    apiGroupedScorecards?.currentInterview ?? [],
    currentUserId,
    tabId,
    canEditOthersScorecard
  );

  const historyScorecards = apiGroupedScorecards?.history ?? [];

  const groupedScorecards = {
    currentInterview: currentInterviewScorecards,
    history: historyScorecards,
    transferInfo: apiGroupedScorecards?.transferInfo ?? null,
  };

  // Early returns for special cases
  if (isCandidateNoShowed) {
    return (
      <EmptyResults
        icon={CsvFailed}
        wrapperProps={{ h: '100%' }}
        text="Undo no-show to fill out the scorecard"
        iconProps={{ h: '140px', w: '142px', mb: '12px' }}
      />
    );
  }

  if (hasNoScorecards) {
    return (
      <EmptyResults
        icon={CsvFailed}
        wrapperProps={{ h: '100%' }}
        text="No scorecards to fill out for this interview"
        iconProps={{ h: '140px', w: '142px', mb: '12px' }}
      />
    );
  }

  // Update: If there is a draft scorecard by other users, we need to show the table view
  // On scorecard tab, if user has their own scorecard, always show form view
  // if (tabId === 'scorecard' && hasCurrentUserScorecard) {
  //   return <AfterInterviewScorecard interview={interview} />;
  // }

  // for HM/Admin, if they have a scorecard, we need to show the form view
  // and the other scorecards in the all scorecards tab

  if (hasCurrentUserScorecard && interview?.can_edit) {
    return <AfterInterviewScorecard interview={interview} />;
  }

  if (
    !useTableView &&
    !canEditOthersScorecard &&
    !isSomeOtherUserHasDraftScorecardForCurrentScorecard
  ) {
    return <AfterInterviewScorecard interview={interview} />;
  }

  return (
    <ScorecardVisibilityView
      groupedScorecards={groupedScorecards}
      currentUserId={currentUserId}
      mode={visibilityMode}
      hasCurrentUserSubmitted={hasCurrentUserSubmitted}
      interview={interview}
      tabId={tabId}
      refetch={refetchScorecardVisibility}
      // isLoading={isLoading}
    />
  );
};
